import { type ReactElement, Suspense } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Navigate } from 'react-router';

import type { RouteResolver } from '~/routing/types';

import { getApiErrorMessage } from '~/common/errors/getApiErrorMessage';
import { getApiErrorStatus } from '~/common/errors/getApiErrorStatus';
import { InlineApiError } from '~/common/errors/InlineApiError';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { FlagType } from '~/flags/flags';
import { useFlags } from '~/flags/useFlags';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { usePermissions } from '~/hooks/usePermissions';
import NotFoundPage from '~/pages/404';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { RbacPermission } from '~/types/Rbac';

import { resolveComponent } from './resolveComponent';

const messages = defineMessages({
  permissionCheckFailedTitle: {
    id: 'RouteGate.permissionCheckFailedTitle',
    defaultMessage: "Can't Confirm Access",
    description:
      'Title shown when the request that checks page access could not be completed | maxLength=40',
  },
  permissionCheckFailedMessage: {
    id: 'RouteGate.permissionCheckFailedMessage',
    defaultMessage: 'We could not confirm your access to this page.',
    description:
      'Message shown when the request that checks page access could not be completed and the server returned no message of its own',
  },
});

/**
 * A 401 here is the auth bootstrap racing the permission check, and pilot's
 * wording for it ("Signature has expired.") means nothing to the person
 * reading it. Any other status is worth showing verbatim — a 503 naming the
 * service that is down tells them something our copy can't.
 */
function resolveApiMessage(error: unknown) {
  if (getApiErrorStatus(error) === 401) return undefined;
  return getApiErrorMessage(error);
}

interface Props {
  path: RoutePathType;
  resolvers: RouteResolver[];
  permissions?: RbacPermission[];
  flag?: FlagType;
}

export function RouteGate({ path, resolvers, permissions, flag }: Props) {
  const flags = useFlags();
  const { params } = useRouting();
  const currentUser = useCurrentUser();

  if (flag && !flags[flag]) {
    return <NotFoundPage />;
  }

  // Resolvers that branch on `permission(user)` need a hydrated user, otherwise
  // they evaluate against an empty user object and fall through to a redirect
  // before the real user loads. Hold rendering until the user is populated.
  const isUserHydrated = Object.keys(currentUser).length > 0;
  const hasPermissionResolvers = resolvers.some(
    (resolver) => resolver.permission,
  );
  if (hasPermissionResolvers && !isUserHydrated) {
    return <ContentContainer loadingContent={{ isLoading: true }} />;
  }

  const context = {
    routeParams: params,
    user: currentUser,
  };

  const Resolved = resolveComponent(resolvers, context);

  // Key by path so React unmounts/remounts on a route change instead of
  // reconciling the same `RouteGate` instance into the new route — which for
  // `PermissionsGate` leaks the previous route's `usePermissions` state.
  const resolved = (
    <Suspense key={path} fallback={null}>
      <Resolved />
    </Suspense>
  );

  // A route that declares no permissions has to leave the gate out of the tree
  // altogether, not rely on the gate to opt out: `usePermissions` runs before
  // any condition inside `PermissionsGate` can be reached, so mounting it on a
  // public route (`/login` and the rest of the clientless family, which are
  // wrapped in `RouteGate` too) subscribes to the permission query and issues
  // `/permission/bulk/` with nobody signed in.
  if (!permissions?.length) {
    return resolved;
  }

  return (
    <PermissionsGate key={path} permissions={permissions}>
      {resolved}
    </PermissionsGate>
  );
}

interface PermissionsGateProps {
  permissions: RbacPermission[];
  children: ReactElement;
}

/**
 * Per-route RBAC gate. Renders children immediately and redirects to
 * /unauthorized once permissions finish loading without access.
 *
 * Three outcomes, not two: granted, denied, and undetermined. Only a check
 * that completed and came back without access may redirect.
 *
 * Mounted only for a route that declares permissions — see `RouteGate`, which
 * owns that branch because this component cannot: reading permissions is a
 * hook call, so it happens before any early return here.
 */
function PermissionsGate({ permissions, children }: PermissionsGateProps) {
  const intl = useIntl();
  const { error, hasSomePermissions, isError, isLoading, refetch } =
    usePermissions({ permissions });

  // Covers retries as well as the first request, so asking again shows the
  // page rather than holding the failure on screen.
  if (isLoading) {
    return children;
  }

  // A check that could not be completed is not a denial. Sending people to
  // /unauthorized on a failed request — a transient 401 during the auth
  // bootstrap, most often — stranded them on an error page that `replace`
  // made unrecoverable without signing out (EKP-63152).
  if (isError) {
    return (
      <InlineApiError
        message={
          resolveApiMessage(error) ??
          intl.formatMessage(messages.permissionCheckFailedMessage)
        }
        onRetry={refetch}
        title={intl.formatMessage(messages.permissionCheckFailedTitle)}
      />
    );
  }

  if (!hasSomePermissions) {
    return <Navigate to={RoutePathType.Unauthorized} replace />;
  }

  return children;
}
